import React, { useEffect } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { SEND_MONEY_RECIPIENT_TYPES, SEND_MONEY_TRANSFER_METHOD_IDS } from "../../constants/enums";
import i18n from "../../i18n/config";
import useGetWorkflow from "../../hooks/sendMoney/useGetWorkflow";
import { ROUTES } from "../../constants/clientRoutes";
import { sortPages, stepSlugs } from "../../services/dynamicForms/workflowAdapter";

// icons
import MyselfIcon from "../../assets/images/icons/rebrand/user.svg";
import SomeoneElseIcon from "../../assets/images/icons/rebrand/users.svg";

// types
import { SendMoneyRecipientType as RecipientType, SendMoneyWorkflowLocationState } from "../../types/sendMoney";
import { RootState } from "../../types/redux";

// ui components
import FlowScreen from "../../components/Layouts/FlowScreen";
import SingleColumnLayout from "../../components/Layouts/SingleColumnLayout";
import RecipientTypeCard from "../../components/SendMoney/RecipientTypeCard";
import SpinningLoader from "../../components/Common/SpinningLoader";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";

const SendMoneyRecipientType = (): React.ReactElement => {
  const { loading, fetchWorkflow } = useGetWorkflow();
  const history = useHistory();

  const transferMethod = useSelector((state: RootState) => state.sendMoney.transferMethod);
  const selectedOperator = useSelector((state: RootState) => state.sendMoney.selectedOperator);
  const quoteId = useSelector((state: RootState) => state.sendMoney.quoteId);

  // Redirect to transfer details page if quoteId is not available
  useEffect(() => {
    if (!quoteId || transferMethod === null) history.replace(ROUTES.SEND_MONEY.TRANSFER_DETAILS);
  }, [quoteId, transferMethod, history]);

  const handleSelect = async (recipientType: RecipientType) => {
    if (!quoteId) return;

    const isMobileWallet = transferMethod === SEND_MONEY_TRANSFER_METHOD_IDS.MOBILE_WALLET;

    const workflow = await fetchWorkflow({
      methodId: transferMethod,
      sendingToSelf: recipientType === SEND_MONEY_RECIPIENT_TYPES.MYSELF,
      quoteId,
      operatorId: isMobileWallet ? selectedOperator?.id : undefined,
    });
    if (!workflow || !workflow.pages.length) return;

    // hand over the quote this workflow was requested for, not the id in the response
    const state: SendMoneyWorkflowLocationState = { workflow, quoteId };
    const [firstStep] = stepSlugs(sortPages(workflow.pages));
    history.push(generatePath(ROUTES.SEND_MONEY.WORKFLOW, { step: firstStep }), state);
  };

  if (loading) return <SpinningLoader show={true} />;

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.SEND_MONEY.RECIPIENT_TYPE.TITLE")}
      showBackButton
      className="v3"
    >
      <SingleColumnLayout>
        <p className="text-secondary">
          {i18n.t("PAGES.SEND_MONEY.RECIPIENT_TYPE.SUBTITLE")}
        </p>

        <RecipientTypeCard
          icon={MyselfIcon}
          label={i18n.t("PAGES.SEND_MONEY.RECIPIENT_TYPE.MYSELF")}
          onSelect={() => handleSelect(SEND_MONEY_RECIPIENT_TYPES.MYSELF)}
        />
        <RecipientTypeCard
          icon={SomeoneElseIcon}
          label={i18n.t("PAGES.SEND_MONEY.RECIPIENT_TYPE.SOMEONE_ELSE")}
          onSelect={() => handleSelect(SEND_MONEY_RECIPIENT_TYPES.SOMEONE_ELSE)}
        />
      </SingleColumnLayout>

      <ApiErrorModal />
    </FlowScreen>
  );
};

export default SendMoneyRecipientType;